/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { brand } from '../../theme/themePrimitives';
import BusinessIcon from '@mui/icons-material/Business';
import CloseDealModal from '../deal/modal/close_deal';
import DealOverviewModal from '../deal/modal/deal_overview';
import DeleteDealModal from './modal/delete_deal';
import FlagIcon from '@mui/icons-material/Flag';
import { getDealsNotesThunk } from '../../redux/thunks/note';
import { getDealsRemindersThunk } from '../../redux/thunks/reminder';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TransferDealModal from '../deal/modal/transfer_deal';
import { updateDealStatusThunk } from '../../redux/thunks/deal';
import { yellow } from '@mui/material/colors';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Checkbox, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { DealObject, PipelineObject, UpdateDealStatusParams } from '../../params/pipeline';
import { DeleteOutlineOutlined, InfoOutlined } from '@mui/icons-material';
import React, { useState } from 'react';
import { selectDealForBulkEdit, settingSelectedPipeline } from '../../redux/slices/pipeline';
import { settingOverviewModal, settingSelectedDeal, settingViewDetail } from '../../redux/slices/deal';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: Deal Component
 * @paragraph: This component represents a single deal in the pipeline. It provides functionalities for viewing, transferring, closing, and updating the deal's status.
 * @interface: DealComponentProps
 * @property: {DealObject} deal - The deal object containing details about the deal.
 */
interface DealComponentProps {
    deal: DealObject;
}

/**
 * @heading1: DealComponent
 * @paragraph: A functional component to display a single deal and its associated actions.
 */
const DealComponent: React.FC<DealComponentProps> = ({ deal }: DealComponentProps) => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * anchorDeal/showTransfer/closeDeal/hovering: React states to store the values for modal and popups.
     * showOverviewModal: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const [, setHovering] = useState<number>(0);
    const [anchorDeal, setAnchorDeal] = useState<null | HTMLElement>(null);
    const [showTransfer, setShowTransfer] = useState<boolean>(false);
    const [closeDeal, setCloseDeal] = useState<boolean>(false);
    const [deleteDeal, setDeleteDeal] = useState<number>(0);
    const { flags } = useSelector((state: RootState) => state.deal);
    const { bulkEditMode, selectedDeals, loaders, selectedPipeline } = useSelector((state: RootState) => state.pipeline);

    /**
     * @heading3: Functions
     * @heading4: fetchNotesAndReminders
     * @paragraph: Fetches notes and reminders for a given deal.
     * @param: {number} id - The ID of the deal.
     */
    const fetchNotesAndReminders = async (id: number) => {
        try {
            await dispatch(getDealsNotesThunk(id));
            await dispatch(getDealsRemindersThunk(id));
        } catch (e) {
            // Catch errors
        }
    };

    /**
     * @heading4: showPopupHandler
     * @paragraph: Prepares the deal for transfer and opens the Transfer Deal modal.
     * @param: {DealObject} comingDeal - The deal to transfer.
     */
    const showPopupHandler = (comingDeal: DealObject) => {
        dispatch(settingSelectedDeal(comingDeal));
        setShowTransfer(true);
    };

    /**
     * @heading4: handleDealMenuClick
     * @paragraph: Opens the deal menu.
     * @param: {React.MouseEvent<HTMLElement>} event - The click event on the menu button.
     */
    const handleDealMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorDeal(event.currentTarget);
    };

    /**
     * @heading4: setDealToState
     * @paragraph: Sets the selected deal to Redux state and opens the Deal Overview modal.
     * @param: {DealObject} comingDeal - The deal to set.
     */
    const setDealToState = (comingDeal: DealObject) => {
        dispatch(settingSelectedDeal(comingDeal));
        dispatch(settingOverviewModal(comingDeal.id));
        fetchNotesAndReminders(comingDeal?.id);
    };

    /**
     * @heading4: handleCloseDeal
     * @paragraph: Closes the deal menu.
     */
    const handleCloseDeal = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setAnchorDeal(null);
    };

    /**
     * @heading4: statusHandler
     * @paragraph: Updates the status of the deal.
     * @param: {number} id - The ID of the deal.
     * @param: {'completed' | 'medium' | 'urgent' | 'overdue' | 'open' | 'archived' | 'cancelled'} status - The new status of the deal.
     */
    const statusHandler = async (id: number, status: 'completed' | 'medium' | 'urgent' | 'overdue' | 'open' | 'archived' | 'cancelled' | 'deleted' | 'closed', event: React.MouseEvent<HTMLLIElement> | undefined) => {
        event?.stopPropagation();
        event?.preventDefault();
        const data: UpdateDealStatusParams = {
            deal_id: id,
            status
        };
        setAnchorDeal(null);
        if (!selectedPipeline) return;
        if (!selectedPipeline.pipeline_stages) return;

        const newStages = [...selectedPipeline.pipeline_stages];
        const selectedStageIndex = newStages.findIndex((item) => item.id === deal.stage_id);
        if (selectedStageIndex === -1) return;

        const selectedDealIndex = newStages[selectedStageIndex].deals.findIndex((item) => item.id === id);
        if (selectedDealIndex === -1) return;

        const updatedDeal = {
            ...newStages[selectedStageIndex].deals[selectedDealIndex],
            status,
            is_archieved: (status === 'archived' ? 1 : 0) as 0 | 1
        };
        const updatedDeals = [...newStages[selectedStageIndex].deals];
        updatedDeals[selectedDealIndex] = updatedDeal;
        const updatedStage = {
            ...newStages[selectedStageIndex],
            deals: updatedDeals
        };

        const updatedStages = [...newStages];
        updatedStages[selectedStageIndex] = updatedStage;

        const updatedPipeline: PipelineObject = {
            ...selectedPipeline,
            pipeline_stages: updatedStages
        };
        dispatch(settingSelectedPipeline(updatedPipeline));
        handleCloseDeal(event);
        try {
            await dispatch(updateDealStatusThunk(data)).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    /**
     * @heading4: onTransferFromOverview
     * @paragraph: Opens the Transfer Deal modal from the Deal Overview modal.
     */
    const onTransferFromOverview = () => {
        dispatch(settingOverviewModal(false));
        setShowTransfer(true);
    };

    /**
     * @heading4: onCloseDealFromOverview
     * @paragraph: Opens the Close Deal modal from the Deal Overview modal.
     */
    const onCloseDealFromOverview = () => {
        dispatch(settingOverviewModal(false));
        setCloseDeal(true);
    };

    const handleViewDeal = () => {
        dispatch(settingSelectedDeal(deal));
        dispatch(settingViewDetail(true));
    };

    const handleBulkSelectionDeal = (dealId: number) => {
        // e.stopPropagation();
        // e.preventDefault();
        dispatch(selectDealForBulkEdit(dealId));
    };

    const deleteDealHandler = (e: any) => {
        e.stopPropagation();
        dispatch(settingSelectedDeal(deal));
        setDeleteDeal(deal.id);
    };

    return <><Box
        onMouseEnter={() => setHovering(deal.id)}
        onMouseLeave={() => setHovering(0)}
        onClick={() => (deal.status === 'pending' ? null : handleViewDeal())}
    >
        {/* Company Name */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box width="100%">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" color="rgba(111,118,126,1)" width="100%">
                        <BusinessIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography width="100%" maxWidth={170} whiteSpace="nowrap" overflow="hidden" boxSizing="border-box" textOverflow="ellipsis" variant="body2" fontSize="12px" fontWeight="600" >
                            {deal.company}
                        </Typography>
                    </Box>
                    <Box>
                        {bulkEditMode && deal.status !== 'pending' && <Box onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={selectedDeals.includes(deal.id)}
                            onChange={() => handleBulkSelectionDeal(deal.id)}
                            disabled={loaders.isLoading}
                        />
                    </Box>
                    }
                    {
                        deal.status === 'pending' && !bulkEditMode && <Typography variant="body2" fontSize="12px" color="error">Processing</Typography>
                    }</Box>
                </Box>
                {/* Deal Title and Actions */}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" whiteSpace="nowrap" overflow="hidden" maxWidth="150px" textOverflow="ellipsis" fontWeight="600">
                        {deal.title}
                    </Typography>
                    <Box display="none">
                        <IconButton size="small" disabled={deal.status === 'pending'} onClick={() => showPopupHandler(deal)}>
                            <SwapHorizIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            {!bulkEditMode && deal.status !== 'pending' && <IconButton onClick={(e) => deleteDealHandler(e)} size="small" color="error"><DeleteOutlineOutlined /></IconButton>}
        </Box>
        {/* Owner and Due Date */}
        <Box mt={1} display="flex" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap="8px">
                <Typography variant="body2">${selectedPipeline?.deal_default_value}</Typography>
            </Box>
            <Box display="flex" alignItems="center">
                <IconButton size="small" disabled={deal.status === 'pending'} onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDealToState(deal);
                }}>
                    <InfoOutlined />
                </IconButton>
                <Box
                    display="flex"
                    alignItems="center"
                    border="1px solid #e0e0e0"
                    borderRadius="4px"
                    padding="4px 8px"
                    bgcolor="#ffffff"
                    onClick={handleDealMenuClick}
                >
                    <FlagIcon sx={{ fontSize: '16px', color: deal.status === 'completed' ? yellow[600] : deal.status === 'medium' ? 'green' : deal.status === 'urgent' ? 'red' : deal.status === 'overdue' ? 'purple' : brand[300], marginRight: '4px' }} />
                    {/* <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: '#000000' }}>
                    {deal.due_date ? formatDate(deal.due_date as string) : ''}
                </Typography> */}
                </Box>
                <Menu
                    anchorEl={anchorDeal}
                    open={Boolean(anchorDeal)}
                    onClose={handleCloseDeal}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={(e) => statusHandler(deal.id, 'completed', e)}>Completed</MenuItem>
                    <MenuItem onClick={(e) => statusHandler(deal.id, 'medium', e)}>Medium</MenuItem>
                    <MenuItem onClick={(e) => statusHandler(deal.id, 'urgent', e)}>Urgent</MenuItem>
                    <MenuItem onClick={(e) => statusHandler(deal.id, 'overdue', e)}>Overdue</MenuItem>
                    {/* <MenuItem onClick={(e) => statusHandler(deal.id, 'cancelled', e)}>Delete</MenuItem> */}
                    <MenuItem onClick={(e) => statusHandler(deal.id, 'archived', e)}>Archive</MenuItem>
                </Menu>
            </Box>
        </Box>
        {/* Modals */}
    </Box>
        {closeDeal && <CloseDealModal open={closeDeal} onClose={() => setCloseDeal(false)} />}
        {showTransfer && <TransferDealModal open={showTransfer} onClose={() => setShowTransfer(false)} />}
        {flags.showOverviewModal === deal.id && <DealOverviewModal
            open={flags.showOverviewModal === deal.id}
            onClose={() => dispatch(settingOverviewModal(0))}
            onTransfer={onTransferFromOverview}
            onCloseDeal={onCloseDealFromOverview} />}
        {deleteDeal === deal.id && <DeleteDealModal open={deleteDeal === deal.id} onClose={() => setDeleteDeal(0)} />}
    </>;
};

export default React.memo(DealComponent);